import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import {
  Building2,
  User,
  MapPin,
  FileText,
  Upload,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';

const COMPANY_TYPES = [
  { value: 'MEI', label: 'MEI' },
  { value: 'ME', label: 'ME' },
  { value: 'EPP', label: 'EPP' },
  { value: 'LTDA', label: 'LTDA' },
  { value: 'SA', label: 'SA' },
  { value: 'EIRELI', label: 'EIRELI' },
  { value: 'SLU', label: 'SLU' },
];

const DOC_TYPES = [
  { value: 'RG', label: 'RG' },
  { value: 'CNH', label: 'CNH' },
  { value: 'CNHPDF', label: 'CNH (PDF)' },
  { value: 'RGPDF', label: 'RG (PDF)' },
  { value: 'Others', label: 'Outro' },
];

const PRODUCT_TYPES = [
  { value: 'SERVICES', label: 'Serviços' },
  { value: 'PRODUCTS', label: 'Produtos' },
  { value: 'BOTH', label: 'Ambos' },
];

interface FormData {
  name: string;
  email: string;
  sellerDocument: string;
  companyType: string;
  fancyName: string;
  sellerName: string;
  sacPhone: string;
  typeProduct: string;
  website: string;
  businessDescription: string;
  legalRepresentativeName: string;
  legalRepresentativeDocument: string;
  legalRepresentativeDocumentType: string;
  legalRepresentativeBornDate: string;
  legalRepresentativePhone: string;
  legalRepresentativeMothersName: string;
  zipCode: string;
  street: string;
  streetNumber: string;
  neighborhood: string;
  addressComplement: string;
  city: string;
  state: string;
  accountEmail: string;
  accountPassword: string;
  phone: string;
}

export function CompanyRegistration() {
  const [form, setForm] = useState<FormData>({
    name: '',
    email: '',
    sellerDocument: '',
    companyType: 'LTDA',
    fancyName: '',
    sellerName: '',
    sacPhone: '',
    typeProduct: 'SERVICES',
    website: '',
    businessDescription: '',
    legalRepresentativeName: '',
    legalRepresentativeDocument: '',
    legalRepresentativeDocumentType: 'RG',
    legalRepresentativeBornDate: '',
    legalRepresentativePhone: '',
    legalRepresentativeMothersName: '',
    zipCode: '',
    street: '',
    streetNumber: '',
    neighborhood: '',
    addressComplement: '',
    city: '',
    state: '',
    accountEmail: '',
    accountPassword: '',
    phone: '',
  });

  const [files, setFiles] = useState<Record<string, File | null>>({
    docSocialContract: null,
    documentCard: null,
    frontDocumentResponsible: null,
    backDocumentResponsible: null,
    photoWithDocument: null,
  });

  const [success, setSuccess] = useState<any>(null);

  const updateField = (field: keyof FormData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field: string, file: File | null) => {
    setFiles(prev => ({ ...prev, [field]: file }));
  };

  const registerMutation = useMutation({
    mutationFn: async () => {
      const formData = new FormData();

      // Add text fields
      for (const [key, value] of Object.entries(form)) {
        if (value) {
          // Clean CNPJ/CPF/CEP
          if (key === 'sellerDocument' || key === 'legalRepresentativeDocument' || key === 'zipCode') {
            formData.append(key, value.replace(/\D/g, ''));
          } else {
            formData.append(key, value);
          }
        }
      }

      // Add files
      for (const [key, file] of Object.entries(files)) {
        if (file) {
          formData.append(key, file);
        }
      }

      const response = await api.post('/admin/anypay/company/register', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: (data) => {
      setSuccess(data);
    },
  });

  const isValid = form.name && form.email && form.sellerDocument.replace(/\D/g, '').length === 14
    && form.companyType && form.legalRepresentativeName
    && form.legalRepresentativeDocument.replace(/\D/g, '').length === 11
    && form.accountEmail && form.accountPassword.length >= 8;

  if (success) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">Registro de Empresa</h1>
        <Card>
          <div className="p-8 text-center space-y-4">
            <CheckCircle size={64} className="mx-auto text-green-400" />
            <h2 className="text-xl font-bold text-white">Empresa registrada com sucesso!</h2>
            <p className="text-surface-300">
              A empresa <strong>{success.data?.company?.name}</strong> foi enviada para análise de compliance.
            </p>
            <p className="text-surface-400">
              A aprovação leva até <strong>48 horas úteis</strong>. Após aprovação, os QR Codes PIX
              gerados sairão no nome da empresa (CNPJ).
            </p>
            <div className="p-4 rounded-lg bg-surface-800 text-left">
              <p className="text-sm text-surface-400">Status: <span className="text-yellow-400 font-medium">Aguardando Compliance</span></p>
              <p className="text-sm text-surface-400">CNPJ: <span className="text-white">{success.data?.company?.cnpj}</span></p>
              <p className="text-sm text-surface-400">ID AtivoPay: <span className="text-white font-mono text-xs">{success.data?.ativopay_response?.id}</span></p>
            </div>
            <Button variant="primary" onClick={() => { setSuccess(null); setForm({ ...form, name: '' }); }}>
              Registrar outra empresa
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Registro de Empresa — PIX Nominal</h1>
        <p className="text-surface-400">
          Registre uma empresa na AtivoPay para que os QR Codes PIX saiam no nome da empresa (CNPJ).
          Após o registro, a empresa passa por análise de compliance (até 48h úteis).
        </p>
      </div>

      {registerMutation.isError && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-red-400 font-medium">Erro ao registrar empresa</p>
            <p className="text-red-300 text-sm">
              {(registerMutation.error as any)?.response?.data?.message || (registerMutation.error as any)?.message || 'Erro desconhecido'}
            </p>
          </div>
        </div>
      )}

      {/* Dados da Empresa */}
      <Card>
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Building2 size={20} className="text-primary-400" />
            Dados da Empresa
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Nome da Empresa *" placeholder="Invest Redenção LTDA" value={form.name} onChange={e => updateField('name', e.target.value)} />
            <Input label="Nome Fantasia" placeholder="Invest Redenção" value={form.fancyName} onChange={e => updateField('fancyName', e.target.value)} />
            <Input label="CNPJ *" placeholder="00.000.000/0000-00" value={form.sellerDocument} onChange={e => updateField('sellerDocument', e.target.value)} />
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">Tipo de Empresa *</label>
              <select className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-white" value={form.companyType} onChange={e => updateField('companyType', e.target.value)}>
                {COMPANY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <Input label="Email da Empresa *" placeholder="contato@empresa.com.br" type="email" value={form.email} onChange={e => updateField('email', e.target.value)} />
            <Input label="Telefone" placeholder="11999999999" value={form.phone} onChange={e => updateField('phone', e.target.value)} />
            <Input label="Website" placeholder="https://empresa.com.br" value={form.website} onChange={e => updateField('website', e.target.value)} />
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">Tipo de Produto/Serviço</label>
              <select className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-white" value={form.typeProduct} onChange={e => updateField('typeProduct', e.target.value)}>
                {PRODUCT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">Descrição do Negócio</label>
            <textarea className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-white min-h-[80px]" placeholder="Descreva o negócio da empresa..." value={form.businessDescription} onChange={e => updateField('businessDescription', e.target.value)} />
          </div>
        </div>
      </Card>

      {/* Representante Legal */}
      <Card>
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <User size={20} className="text-primary-400" />
            Representante Legal
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Nome Completo *" placeholder="João da Silva" value={form.legalRepresentativeName} onChange={e => updateField('legalRepresentativeName', e.target.value)} />
            <Input label="CPF *" placeholder="000.000.000-00" value={form.legalRepresentativeDocument} onChange={e => updateField('legalRepresentativeDocument', e.target.value)} />
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">Tipo de Documento *</label>
              <select className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-white" value={form.legalRepresentativeDocumentType} onChange={e => updateField('legalRepresentativeDocumentType', e.target.value)}>
                {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <Input label="Data de Nascimento" type="date" value={form.legalRepresentativeBornDate} onChange={e => updateField('legalRepresentativeBornDate', e.target.value)} />
            <Input label="Telefone" placeholder="11999999999" value={form.legalRepresentativePhone} onChange={e => updateField('legalRepresentativePhone', e.target.value)} />
            <Input label="Nome da Mãe" placeholder="Maria da Silva" value={form.legalRepresentativeMothersName} onChange={e => updateField('legalRepresentativeMothersName', e.target.value)} />
          </div>
        </div>
      </Card>

      {/* Endereço */}
      <Card>
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <MapPin size={20} className="text-primary-400" />
            Endereço
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="CEP" placeholder="01001000" value={form.zipCode} onChange={e => updateField('zipCode', e.target.value)} />
            <Input label="Rua" placeholder="Rua Exemplo" value={form.street} onChange={e => updateField('street', e.target.value)} />
            <Input label="Número" placeholder="123" value={form.streetNumber} onChange={e => updateField('streetNumber', e.target.value)} />
            <Input label="Bairro" placeholder="Centro" value={form.neighborhood} onChange={e => updateField('neighborhood', e.target.value)} />
            <Input label="Complemento" placeholder="Sala 101" value={form.addressComplement} onChange={e => updateField('addressComplement', e.target.value)} />
            <Input label="Cidade" placeholder="São Paulo" value={form.city} onChange={e => updateField('city', e.target.value)} />
            <Input label="Estado (UF)" placeholder="SP" maxLength={2} value={form.state} onChange={e => updateField('state', e.target.value.toUpperCase())} />
          </div>
        </div>
      </Card>

      {/* Conta de Acesso */}
      <Card>
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FileText size={20} className="text-primary-400" />
            Conta de Acesso (Painel AtivoPay)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Email da Conta *" placeholder="conta@empresa.com.br" type="email" value={form.accountEmail} onChange={e => updateField('accountEmail', e.target.value)} />
            <Input label="Senha *" placeholder="Mínimo 8 caracteres" type="password" value={form.accountPassword} onChange={e => updateField('accountPassword', e.target.value)} />
          </div>
        </div>
      </Card>

      {/* Documentos */}
      <Card>
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Upload size={20} className="text-primary-400" />
            Documentos (opcional — podem ser solicitados depois)
          </h2>
          <p className="text-sm text-surface-400">Formatos aceitos: PDF, JPG, PNG. Máximo 10MB por arquivo.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { field: 'docSocialContract', label: 'Contrato Social (PDF)' },
              { field: 'documentCard', label: 'Cartão CNPJ' },
              { field: 'frontDocumentResponsible', label: 'Documento do Representante (frente)' },
              { field: 'backDocumentResponsible', label: 'Documento do Representante (verso)' },
              { field: 'photoWithDocument', label: 'Selfie com Documento' },
            ].map(({ field, label }) => (
              <div key={field}>
                <label className="block text-sm font-medium text-surface-300 mb-1">{label}</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => handleFileChange(field, e.target.files?.[0] || null)}
                  className="w-full text-sm text-surface-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-500/20 file:text-primary-400 hover:file:bg-primary-500/30"
                />
                {files[field] && (
                  <p className="text-xs text-green-400 mt-1">✓ {files[field]!.name}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Submit */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          size="lg"
          onClick={() => registerMutation.mutate()}
          disabled={!isValid || registerMutation.isPending}
        >
          {registerMutation.isPending ? (
            <>
              <Loader2 size={20} className="mr-2 animate-spin" />
              Registrando...
            </>
          ) : (
            <>
              <Building2 size={20} className="mr-2" />
              Registrar Empresa
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
